import React from 'react';
import { SearchableSelect } from '@shared/components';
import { useInfiniteItemsQuery, useItemsByIds } from '../hooks/useItemsQuery';
import { flattenItemPages } from '../utils/flattenItemPages';
import { itemPickerParams } from '../utils/itemPickerParams';
import type { ItemRecord } from '../services/smr-admin';

export type ItemOption = Pick<ItemRecord, 'id' | 'name' | 'code'>;

interface Props {
    value: string;
    onChange: (id: string, item: ItemOption | null) => void;
    excludeIds?: string[];
    suppressForbiddenToast?: boolean;
    disabled?: boolean;
    id?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
}

export function ItemCombobox({
    value,
    onChange,
    excludeIds = [],
    suppressForbiddenToast,
    disabled = false,
    id,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
}: Props) {
    const [term, setTerm] = React.useState('');
    const [picked, setPicked] = React.useState<ItemOption | null>(null);
    const { data, isLoading, isFetchingNextPage, isError, isFetchNextPageError, hasNextPage, fetchNextPage } = useInfiniteItemsQuery(
        itemPickerParams(term, excludeIds.length),
        { suppressForbiddenToast },
    );
    const results: ItemOption[] = flattenItemPages(data?.pages);
    const loadMore = () => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    };
    const needsLookup = value !== '' && picked?.id !== value;
    const { data: byIds } = useItemsByIds(needsLookup ? [value] : []);

    const findById = (list: ItemOption[] | undefined) => list?.find((o) => o.id === value);
    let selected: ItemOption | null = null;
    if (value !== '') {
        selected = picked?.id === value ? picked : (findById(results) ?? findById(byIds) ?? null);
    }

    return (
        <SearchableSelect<ItemOption>
            options={results}
            value={selected}
            onChange={(option) => {
                setPicked(option);
                onChange(option?.id ?? '', option);
            }}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            onSearchChange={setTerm}
            loading={isLoading || isFetchingNextPage}
            showToggle
            hasMore={hasNextPage}
            onLoadMore={loadMore}
            loadMoreError={isFetchNextPageError ? 'Could not load more items.' : undefined}
            excludeValues={excludeIds}
            disabled={disabled}
            id={id}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            placeholder="Select or search items…"
            emptyLabel={isError ? 'Could not load items.' : 'No items match.'}
            renderOption={(o) =>
                o.code ? (
                    <>
                        {o.name}
                        <span className="text-mp-muted text-xs ml-1">({o.code})</span>
                    </>
                ) : (
                    o.name
                )
            }
        />
    );
}
